import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { getGrowthShrimpChartKey, getGrowthShrimpsKey, getGrowthShrimpTodayKey } from 'common/constants/queryKeys'
import useCycleApi from 'common/hooks/useCycleApi'
import { BasePaginationResponseV2, BaseResponse } from 'common/types/api'
import { formatDate } from 'common/utils/date'
import { selectedCycleIdState } from 'modules/pond/stores/atom'
import { useRecoilValue } from 'recoil'

import { GROWTH_REQUEST_CONFIG } from '.'
import { growthShrimpDateTableFilterState, growthShrimpEndDateTableFilterState, growthShrimpStartDateTableFilterState } from '../stores/atom'
import { GrowthShrimp, GrowthShrimpLineChart, GrowthShrimpToday } from './types'

const ROWS_PER_PAGE = 10

export const useGetGrowthShrimpLineChart = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getGrowthShrimpChartKey(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<GrowthShrimpLineChart[]>>(
        GROWTH_REQUEST_CONFIG.getGrowthLineChart
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetGrowthShrimps = (enabled: boolean = true) => {
  const cycleApi = useCycleApi()
  const date = useRecoilValue(growthShrimpDateTableFilterState)
  const startDate = useRecoilValue(growthShrimpStartDateTableFilterState)
  const endDate = useRecoilValue(growthShrimpEndDateTableFilterState)
  const cycleId = useRecoilValue(selectedCycleIdState)
  const initParams = {
    params: {
      pageSize: ROWS_PER_PAGE,
      page: 1,
      startDate: startDate ? formatDate(startDate): '',
      endDate: endDate ? formatDate(endDate) : ''
    },
    cycleId,
  }

  return useInfiniteQuery({
    queryKey: getGrowthShrimpsKey(cycleId || '', startDate, endDate),
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await cycleApi.request<BasePaginationResponseV2<GrowthShrimp[]>>({
        ...GROWTH_REQUEST_CONFIG.getGrowths,
        ...pageParam,
      })
      return {
        shrimps: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage, _) => {
      if (lastPage.nextPage) {
        return {
          cycleId: initParams.cycleId,
          params: { ...initParams.params, page: lastPage.nextPage },
        }
      } else {
        return undefined
      }
    },
    staleTime: Infinity,
    enabled: enabled && !!cycleId,
  })
}

export const useGetGrowthShrimpToday = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useInfiniteQuery(
    {
      queryKey: getGrowthShrimpTodayKey(cycleId || ''),
      queryFn: async () => {
        const {data} = await cycleApi.request<BaseResponse<GrowthShrimpToday>>(
              GROWTH_REQUEST_CONFIG.getGrowthToday,
      )
        return data.data  
      },
      staleTime: Infinity,
      enabled: !!cycleId,
    }
  )
}
